import { IconButton, styled, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useRef } from 'react';
import {
  DeleteOutlined,
  DragHandle,
  EditOutlined,
  DateRange,
  LocationOn,
} from '@mui/icons-material';
import { useDrag, useDrop } from 'react-dnd';
import type { Identifier, XYCoord } from 'dnd-core';
import { ExternalMatchRequestRule } from 'generated/graphql';

import RuleDescription from './RuleDescription';
import StatusDot from './StatusDot';
import { RuleTypeChip } from '../components/RuleTypeChip';
import { getFormattedDateRangeWithMultiDaySupport } from 'utils/getFormattedDateRangeWithMultiDaySupport';

const ListItemWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '54px 54px minmax(200px, 800px) minmax(108px,200px) 76px 1fr 54px 54px',
  alignItems: 'center',
  justifyItems: 'center',
  gap: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  borderRadius: '4px',
  boxShadow: '0 3px 4px 0 rgba(0, 0, 0, 0.02)',
  cursor: 'move',
}));

interface RuleListItemProps extends ExternalMatchRequestRule {
  index: number;
  moveRule: (dragIndex: number, hoverIndex: number) => void;
  onDragEnd?: () => void;
  onRemove: () => void;
  onEdit: () => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const RuleListItem: React.FC<RuleListItemProps> = ({
  id,
  index,
  type,
  active,
  startingAt,
  endingAt,
  confidenceScore,
  minAmount,
  maxAmount,
  employeeLocations,
  moveRule,
  onDragEnd,
  onRemove,
  onEdit,
}) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: 'rule',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveRule(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>({
    item: { id, index, type: 'rule' },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      onDragEnd?.();
    },
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  return (
    <ListItemWrapper ref={ref} style={{ opacity }} data-handler-id={handlerId}>
      <div
        style={{
          color: theme.palette.primary.main,
          justifySelf: 'center',
        }}
      >
        <DragHandle fontSize="large" />
      </div>

      <Typography fontWeight="500" fontSize="1rem" sx={{ justifySelf: 'center' }}>
        {index + 1}
      </Typography>

      <div style={{ justifySelf: 'start' }}>
        <RuleDescription
          confidenceScore={confidenceScore}
          minAmount={minAmount}
          maxAmount={maxAmount}
          type={type}
        />
      </div>

      <div style={{ justifySelf: 'start' }}>
        <RuleTypeChip active={active} type={type} />
      </div>

      <div style={{ display: 'flex', gap: theme.spacing(2), justifySelf: 'end' }}>
        {Boolean(employeeLocations?.length) ? (
          <Tooltip
            title={`Rule applies to selected locations only.`}
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: '14px',
                  padding: '8px 12px',
                },
              },
            }}
          >
            <div>
              <LocationOn
                fontSize="large"
                sx={{
                  color: active ? theme.palette.primary.main : theme.palette.grey[400],
                }}
              />
            </div>
          </Tooltip>
        ) : null}

        {startingAt && endingAt ? (
          <Tooltip
            title={(() => {
              const { datePart, utcPart } = getFormattedDateRangeWithMultiDaySupport({
                startingAt,
                timeZone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
                endingAt,
                withTime: true,
              });
              return `Rule applies from ${datePart} ${utcPart}.`;
            })()}
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: '14px',
                  padding: '8px 12px',
                },
              },
            }}
          >
            <div>
              <DateRange
                fontSize="large"
                sx={{
                  color: active ? theme.palette.primary.main : theme.palette.grey[400],
                }}
              />
            </div>
          </Tooltip>
        ) : null}
      </div>
      <div style={{ justifySelf: 'end' }}>
        <div
          style={{
            width: '200px',
            justifySelf: 'start',
            marginLeft: theme.spacing(4),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          <StatusDot active={active} />
          <Typography
            fontWeight="400"
            style={{ marginTop: '2px', letterSpacing: '0.03em', textTransform: 'uppercase' }}
          >
            {active ? 'Active' : 'Inactive'}
          </Typography>
        </div>
      </div>

      <IconButton
        onClick={onRemove}
        sx={{
          color: theme.palette.primary.main,
        }}
        size="large"
      >
        <DeleteOutlined fontSize="large" />
      </IconButton>

      <IconButton
        onClick={onEdit}
        sx={{
          color: theme.palette.primary.main,
        }}
        size="large"
      >
        <EditOutlined fontSize="large" />
      </IconButton>
    </ListItemWrapper>
  );
};

export default RuleListItem;
